import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, colones, fmtFecha, todayISO } from "@/lib/api";
import { Heart, Plus, X, Pencil, Trash2, ChevronLeft, ChevronRight, Calendar, FileText, AlertTriangle, FileDown } from "lucide-react";
import ReportModal from "@/components/ReportModal";
import { matchesFrecuencia, empleadoActivoEn } from "@/lib/frecuencia";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const TIPOS = ["CCSS", "INS"];

const TIPO_BADGE = {
  "CCSS": "np-badge-info",
  "INS":  "np-badge-warning",
};

function daysBetween(a, b) {
  if (!a || !b) return 0;
  const d1 = new Date(a + "T00:00:00");
  const d2 = new Date(b + "T00:00:00");
  return Math.round((d2 - d1) / 86400000) + 1;
}

const IncapacidadModal = ({ incap, empleados, onClose, onSaved }) => {
  const [data, setData] = useState(incap || { tipo: "CCSS", fecha_inicio: todayISO(), fecha_fin: todayISO() });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));
  const dias = daysBetween(data.fecha_inicio, data.fecha_fin);

  const empSeleccionado = empleados.find(e => e.id === data.empleado_id);
  const minFecha = empSeleccionado?.fecha_ingreso || undefined;
  const fechaInvalida = empSeleccionado && minFecha && data.fecha_inicio && data.fecha_inicio < minFecha;

  const save = async (e) => {
    e.preventDefault();
    if (fechaInvalida) {
      setErr(`La fecha de inicio no puede ser anterior al ingreso del empleado (${minFecha}).`);
      return;
    }
    setErr(""); setSaving(true);
    try {
      if (incap?.id) await api.put(`/incapacidades/${incap.id}`, data);
      else await api.post("/incapacidades", data);
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl fade-in-up" onClick={ev => ev.stopPropagation()} data-testid="incap-modal">
        <div className="p-6 border-b border-c flex justify-between">
          <h3 className="font-display text-2xl font-semibold">{incap ? "Editar incapacidad" : "Nueva incapacidad"}</h3>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="np-label">Empleado *</label>
            <select required className="np-input" value={data.empleado_id || ""} onChange={e => set("empleado_id", e.target.value)} data-testid="incap-empleado">
              <option value="">Seleccionar empleado</option>
              {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>)}
            </select>
          </div>

          <div>
            <label className="np-label">Tipo de incapacidad *</label>
            <div className="grid grid-cols-2 gap-2">
              {TIPOS.map(t => (
                <button key={t} type="button" onClick={() => set("tipo", t)} data-testid={`incap-tipo-${t}`}
                  className={`p-3 rounded-xl border text-left transition-all ${data.tipo === t ? "border-[var(--primary)] bg-[#E6EFEA]" : "border-c bg-white hover:border-[var(--primary)]"}`}>
                  <div className="font-medium text-sm">{t}</div>
                  <div className="text-[11px] text-secondary-c mt-0.5">
                    {t === "CCSS" ? "3 días pagos por patrón, resto por CCSS" : "No se paga, lo cubre el INS (riesgo del trabajo)"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="np-label">Fecha de inicio *</label>
              <input type="date" required min={minFecha} className="np-input" value={data.fecha_inicio || ""} onChange={e => set("fecha_inicio", e.target.value)} data-testid="incap-inicio" />
            </div>
            <div>
              <label className="np-label">Fecha de fin *</label>
              <input type="date" required min={data.fecha_inicio || minFecha} className="np-input" value={data.fecha_fin || ""} onChange={e => set("fecha_fin", e.target.value)} data-testid="incap-fin" />
            </div>
          </div>
          {empSeleccionado?.fecha_ingreso && (
            <p className="text-[11px] text-secondary-c -mt-2">Fecha mínima: {empSeleccionado.fecha_ingreso} (ingreso del empleado)</p>
          )}
          {fechaInvalida && <p className="text-xs text-error font-medium -mt-2">La fecha de inicio es anterior al ingreso del empleado.</p>}

          {dias > 0 && (
            <div className="p-3 rounded-xl bg-[#FAFAF7] border border-c flex items-center justify-between text-sm">
              <span className="text-secondary-c">Días totales</span>
              <span className="font-display font-semibold text-lg" data-testid="incap-dias-total">{dias} {dias === 1 ? "día" : "días"}</span>
            </div>
          )}

          {data.tipo === "CCSS" && dias > 0 && (
            <div className="p-3 rounded-xl bg-[#FFF4E5] border border-[#F2DFB6] text-xs text-[#A67115] flex gap-2 items-start">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                El patrón paga los primeros <b>3 días al 50 %</b> ({Math.min(3, dias) * 4} h, medio día c/u).
                {dias > 3 && <> Los <b>{dias - 3} días restantes</b> los cubre la CCSS y NO se pagan al empleado por planilla.</>}
              </div>
            </div>
          )}

          <div>
            <label className="np-label">Diagnóstico / Descripción</label>
            <textarea className="np-input" rows={2} value={data.descripcion || ""} onChange={e => set("descripcion", e.target.value)} placeholder="N° de boleta, diagnóstico, etc. (opcional)" />
          </div>

          {err && <div className="p-3 rounded-xl bg-[#FDEDED] text-[#B84B4B] text-sm">{String(err)}</div>}

          <div className="flex justify-end gap-3 pt-2 border-t border-c">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving || fechaInvalida} className="np-btn np-btn-primary" data-testid="incap-submit">
              {saving ? "Guardando..." : (incap ? "Guardar" : "Registrar incapacidad")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Incapacidades() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [tipo, setTipo] = useState("semanal"); // semanal | quincenal
  const [filtroEmp, setFiltroEmp] = useState("");
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [open, setOpen] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [empresa, setEmpresa] = useState({});

  useEffect(() => { api.get("/empresa").then(r => setEmpresa(r.data || {})).catch(() => {}); }, []);

  const generarReporte = async ({ format, fechaInicio, fechaFin, periodoLabel }) => {
    const { data } = await api.get(`/incapacidades?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
    const { reporteIncapacidades } = await import("@/lib/reportes");
    reporteIncapacidades({ format, items: data || [], empleados, periodoLabel, empresa });
  };

  const load = async () => {
    const [i, e] = await Promise.all([
      api.get(`/incapacidades?year=${year}&month=${month + 1}`),
      api.get("/empleados"),
    ]);
    setItems(i.data); setEmpleados(e.data);
  };
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const empById = Object.fromEntries(empleados.map(e => [e.id, e]));
  const empleadosFiltrados = useMemo(() => empleados.filter(e => e.estado === "Activo" && matchesFrecuencia(e, tipo)), [empleados, tipo]);

  const filtered = useMemo(() => {
    return items.filter(r => {
      const emp = empById[r.empleado_id];
      if (!emp || !matchesFrecuencia(emp, tipo)) return false;
      if (filtroEmp && r.empleado_id !== filtroEmp) return false;
      return true;
    });
  }, [items, filtroEmp, empById, tipo]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const totalDias = filtered.reduce((s, r) => s + (r.dias_total || daysBetween(r.fecha_inicio, r.fecha_fin)), 0);
    const ccss = filtered.filter(r => r.tipo === "CCSS").length;
    const ins = filtered.filter(r => r.tipo === "INS").length;
    return { total, totalDias, ccss, ins };
  }, [filtered]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta incapacidad? Se revertirán las asistencias asociadas.")) return;
    await api.delete(`/incapacidades/${id}`);
    load();
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FDEDED] text-error flex items-center justify-center"><Heart size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Incapacidades</h1>
            <p className="text-sm text-secondary-c">Registro de incapacidades CCSS e INS · {MONTHS_ES[month]} {year}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="incap-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={() => setOpen("new")} className="np-btn np-btn-primary" data-testid="new-incap-btn"><Plus size={16} /> Nueva incapacidad</button>
        </div>
      </header>

      {showReport && (
        <ReportModal title="Reporte de Incapacidades" mode="periodo" onClose={() => setShowReport(false)} onGenerate={generarReporte} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          { label: "Total registros", value: stats.total, icon: FileText, color: "primary" },
          { label: "Días acumulados", value: stats.totalDias, icon: Calendar, color: "accent" },
          { label: "CCSS", value: stats.ccss, icon: Heart, color: "info" },
          { label: "INS", value: stats.ins, icon: AlertTriangle, color: "warning" },
        ].map(s => {
          const Ic = s.icon;
          return (
            <div key={s.label} className="np-card !p-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${s.color === "accent" ? "bg-[#FDF1EC] text-[#D96C4A]" : s.color === "warning" ? "bg-[#FFF4E5] text-[#A67115]" : s.color === "info" ? "bg-[#E5EEF2] text-[#2D5B6E]" : "bg-[#E6EFEA] text-[#274735]"}`}>
                <Ic size={16} />
              </div>
              <div className="font-display text-2xl font-semibold">{s.value}</div>
              <div className="text-xs text-secondary-c mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="np-card p-4 mb-5 flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-xl bg-[#EFEEE8] p-1">
          <button onClick={() => setTipo("semanal")} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "semanal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="incap-tab-semanal">Semanal</button>
          <button onClick={() => setTipo("quincenal")} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "quincenal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="incap-tab-quincenal">Quincenal</button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="np-btn np-btn-outline p-2" data-testid="incap-prev"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium font-display" data-testid="incap-month-label">{MONTHS_ES[month]} {year}</div>
          <button onClick={nextMonth} className="np-btn np-btn-outline p-2" data-testid="incap-next"><ChevronRight size={16} /></button>
        </div>
        <select className="np-input w-auto" value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} data-testid="incap-filter-empleado">
          <option value="">Todos los empleados ({tipo === "semanal" ? "semanales" : "quincenales"})</option>
          {empleadosFiltrados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>)}
        </select>
      </div>

      <div className="np-card p-0 np-scroll">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><Heart size={36} className="mx-auto text-secondary-c opacity-40 mb-3" /><div className="font-medium">Sin incapacidades en este mes</div><div className="text-sm text-secondary-c">Crea una para llevar el registro y aplicarla automáticamente en planilla.</div></div>
        ) : (
          <table className="np-table">
            <thead><tr>
              <th>Empleado</th><th>Tipo</th><th>Desde</th><th>Hasta</th><th>Días</th><th>Días pagos</th><th>Descripción</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map(r => {
                const emp = empById[r.empleado_id];
                const dias = r.dias_total || daysBetween(r.fecha_inicio, r.fecha_fin);
                const diasPagos = r.tipo === "CCSS" ? Math.min(3, dias) : 0;
                return (
                  <tr key={r.id} data-testid={`incap-row-${r.id}`}>
                    <td className="font-medium">{emp ? `${emp.nombre} ${emp.apellidos}` : "—"}</td>
                    <td><span className={`np-badge ${TIPO_BADGE[r.tipo]}`}>{r.tipo}</span></td>
                    <td>{fmtFecha(r.fecha_inicio)}</td>
                    <td>{fmtFecha(r.fecha_fin)}</td>
                    <td className="font-semibold">{dias}</td>
                    <td>
                      <span className={diasPagos > 0 ? "text-success font-medium" : "text-secondary-c"}>{diasPagos} {diasPagos === 1 ? "día" : "días"}</span>
                      {r.tipo === "CCSS" && diasPagos > 0 && <span className="text-secondary-c text-xs"> · {diasPagos * 4}h (50%)</span>}
                      {r.tipo === "CCSS" && dias > 3 && <span className="text-secondary-c text-xs block">{dias - 3} no pagos</span>}
                      {r.tipo === "INS" && <span className="text-secondary-c text-xs"> · INS paga directo</span>}
                    </td>
                    <td className="text-secondary-c text-sm max-w-[200px] truncate">{r.descripcion || "—"}</td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setOpen({ incap: r })} className="np-btn np-btn-ghost p-2"><Pencil size={15} /></button>
                        <button onClick={() => remove(r.id)} className="np-btn np-btn-ghost p-2 text-error"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && <IncapacidadModal incap={open === "new" ? null : open.incap} empleados={empleadosFiltrados} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </Layout>
  );
}
